import React from 'react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import Dropdown from '@SonarCX/components/atoms/Dropdown'
import TaskStatus from '@SonarCX/components/molecules/RenderOptions/TaskStatus'

import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { selectTaskPriority } from '@SonarCX/slices/priority'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import useTaskPermissions from '@SonarCX/hooks/permissions/useTaskPermissions'
import useTaskInputOpen from '@SonarCX/hooks/useTaskInputOpen'
import useStyles from './styles'

type PriorityProps = {
  task: LocalTask & { table?: any }
}

const Priority: React.FC<PriorityProps> = ({ task }) => {
  const dispatch = useAppDispatch()

  const { priorities } = useAppSelector(selectTaskPriority)

  const { updateTask } = useTaskAPI()
  const { disabled } = useTaskPermissions(task)
  const { isOpen, handleClick, handleClose } = useTaskInputOpen(
    task,
    'PRIORITY'
  )

  const [startingValue, setStartingValue] = React.useState(task.priority_id)
  const [value, setValue] = React.useState(startingValue)

  const classes = useStyles({ disabled })

  React.useEffect(() => {
    setStartingValue(task.priority_id)
    setValue(task.priority_id)
  }, [task.priority_id])

  const handleSelect = React.useCallback(
    async value => {
      handleClose()
      if (startingValue !== value) {
        try {
          const newTask = { ...task, priority_id: value }
          await updateTask(newTask.id, newTask)
          setValue(value)
          dispatch(AddSuccessAlert('Changes saved'))
        } catch (err) {
          dispatch(AddErrorAlert({ err }))
          setValue(startingValue)
        }
      }
    },
    [dispatch, startingValue, task, updateTask, handleClose]
  )

  const renderChosen = React.useMemo(() => {
    if (!value) {
      return <TaskStatus opt={{ name: 'Unassigned' }} testId='priority' />
    }

    return (
      <TaskStatus
        opt={priorities.find(({ id }) => id === value) ?? {}}
        testId='priority'
      />
    )
  }, [priorities, value])

  return (
    <div className={classes.root}>
      {isOpen ? (
        <Dropdown
          options={priorities.map(priority => priority.id)}
          renderOption={id => {
            const opt = priorities.find(s => s.id === id)

            if (opt) {
              return <TaskStatus key={id} opt={opt} testId='priority' />
            }
            return null
          }}
          selected={value}
          onSelect={handleSelect}
          onClose={handleClose}
          hideAdd
          colour
          forceOpen
          disabled={disabled}
        >
          {renderChosen}
        </Dropdown>
      ) : (
        <div onClick={handleClick}>{renderChosen}</div>
      )}
    </div>
  )
}

export default React.memo(Priority)
